/* eslint-disable no-unused-vars */
import * as d3 from "d3";
import isFunction from "lodash/isFunction";
import {
  colorRangeDefault,
  linkDarkStyle,
  linkLightStyle,
  nodeCommonStyle,
  nodeDarkStyle,
  nodeLightStyle,
  subGroupCssStyle,
} from "../networkConfig";
import { drag, getLineCurve, escapeRegExp } from "./SNAutils";

const drawNetwork = ({
  myRef,
  svg: cSvg,
  view: cView,
  data,
  groups,
  backgroundStyle,
  addNodeCount,
  legendShowNodDataGroup,
  showSubGroup,
  placeNodeConfig,
  placeNodeMode,
  statisticDispersion, // 節點放置位置的離散程度 (1~10)
  snaColors,
  snaWidth,
  snaHeight,
  onClickNode,
  onMouseOverNode,
  onMouseOutNode,
  onClickLink,
  datasetTotal,
}) => {
  const width = snaWidth || myRef?.current?.offsetWidth || window.innerWidth;
  const height =
    snaHeight || myRef?.current?.offsetHeight || window.innerHeight;
  // 線條樣式
  const linkStyle = backgroundStyle === "dark" ? linkDarkStyle : linkLightStyle;
  // 節點樣式
  const nodeStyle = backgroundStyle === "dark" ? nodeDarkStyle : nodeLightStyle;
  // subGroup 樣式
  const subGroupStyle = subGroupCssStyle;
  // 色盤
  // const colorRange =
  //   backgroundStyle === "dark" ? colorRangeDefault : colorRangeDefault;
  const colorRange = snaColors || colorRangeDefault;
  const nodeLabelSize = 12;
  const lineStroke = 3.0;
  const lineRadius = 0;
  const nodeStroke = 1.5;
  const nodeRadius = 10 - nodeStroke;
  const arrowPercent = 0;
  const arrowViewBox = `-0 -${nodeRadius} ${nodeRadius * 2} ${nodeRadius * 2}`;
  const arrowPath = `M 0,-${nodeRadius * arrowPercent} L ${
    nodeRadius * arrowPercent * 2
  } ,0 L 0,${nodeRadius * arrowPercent}`;

  const { currentGroups, curSubGroups } = data;

  const groupsForColorRange = legendShowNodDataGroup ? groups : currentGroups;
  const groupColor = d3
    .scaleOrdinal()
    .domain(groupsForColorRange)
    .range(colorRange);

  if (!data.links || !data.nodes) return;

  const links = data.links.map((d) => Object.create(d));
  const nodes = data.nodes.map((d) => Object.create(d));

  const linkColor = d3
    .scaleLinear()
    .domain([
      d3.min(data.links, (d) => d.count),
      d3.max(data.links, (d) => d.count),
    ])
    .range([`${linkStyle.color.min}`, `${linkStyle.color.max}`]);

  const linkOpacity = d3
    .scaleLinear()
    .domain([
      d3.min(data.links, (d) => d.count),
      d3.max(data.links, (d) => d.count),
    ])
    .range([0.3, 1.0]);

  const nodeSize = d3
    .scaleLinear()
    .domain([0, d3.max(data.nodes, (d) => d.count)])
    .range([
      `${nodeCommonStyle.nodeSize.min}`,
      `${nodeCommonStyle.nodeSize.max}`,
    ]);

  const distanceFromCenter = d3
    .scaleLinear()
    .domain([0, d3.max(data.nodes, (d) => d.count)])
    .range([
      placeNodeConfig.centerAndEdge.lgRadius * (statisticDispersion / 10),
      placeNodeConfig.centerAndEdge.smRadius,
    ]);

  // 計算 d3.forceRadial 的半徑  => version2
  const calcRadius = (placeMode, d) => {
    if (placeMode === placeNodeConfig.center.name)
      return placeNodeConfig.center.radius;
    if (placeMode === placeNodeConfig.edge.name)
      return placeNodeConfig.edge.radius;
    if (placeMode === placeNodeConfig.centerAndEdge.name)
      return distanceFromCenter(d.count); // 節點由內而外依節點大小層次擴散
    return distanceFromCenter(d.count);
  };

  const simulation = d3
    .forceSimulation(nodes)
    /** link: link 的拉力 */
    .force(
      "link",
      d3
        .forceLink(links)
        .id((d) => d.id)
        .distance(500)
    )
    /** charge: node 與 node 的引力 */
    .force("charge", d3.forceManyBody().strength(-160))
    /** center: 重力中心 */
    .force("center", d3.forceCenter(width / 2, height / 2))
    /** 碰撞(collide)偵測 */
    .force(
      "collide",
      d3
        .forceCollide()
        .radius((d) => nodeSize(d.count) * nodeRadius + 5)
        .iterations(5)
    )
    .force("x", d3.forceX(width / 2).strength(1))
    .force("y", d3.forceY(height / 2).strength(1))
    /** force(r, foreRadial(radiius, x, y)) => put node centered or edged */
    .force(
      "r",
      d3
        .forceRadial((d) => calcRadius(placeNodeMode, d), width / 2, height / 2)
        .strength(
          (d) =>
            // default: 0.1(move more smooth)
            // max: 1(move not smooth)
            1
        )
    );

  d3.select(myRef.current).select("svg").remove();

  const zoom = d3
    .zoom()
    .scaleExtent([-40, 40])
    // eslint-disable-next-line no-use-before-define
    .on("zoom", zoomed);

  function zoomed() {
    d3.select(".view").attr("transform", d3.event.transform);
  }

  let svg;
  if (cSvg) {
    svg = cSvg;
  } else {
    svg = d3
      .select(myRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .call(zoom);
  }
  // const svg = d3
  //   .select(myRef.current)
  //   .append("svg")
  //   .attr("width", width)
  //   .attr("height", height)
  //   .attr("viewBox", [0, 0, width, height])
  //   .call(zoom);

  let view;
  if (cView) {
    view = cView;
  } else {
    view = svg
      .append("g")
      .attr("class", "view")
      .attr("x", 0.5)
      .attr("y", 0.5)
      .attr("width", width - 1)
      .attr("height", height - 1);
  }

  // const view = svg
  //   .append("g")
  //   .attr("class", "view")
  //   .attr("x", 0.5)
  //   .attr("y", 0.5)
  //   .attr("width", width - 1)
  //   .attr("height", height - 1);

  svg
    .append("defs")
    .append("marker")
    .attr("id", "arrowhead")
    .attr("viewBox", arrowViewBox)
    .attr("refX", nodeRadius * 2 + nodeRadius * arrowPercent)
    .attr("refY", 0)
    .attr("orient", "auto")
    .attr("markerWidth", nodeRadius * (1 + arrowPercent))
    .attr("markerHeight", nodeRadius * (1 + arrowPercent))
    .attr("xoverflow", "visible")
    .append("svg:path")
    .attr("d", arrowPath)
    .attr("fill", "#999")
    .style("stroke", "none");

  const link = view
    .append("g")
    .selectAll("line")
    .data(links, (d) => d.id)
    .join("path")
    .attr("class", "link")
    .attr("data-id", (d) => `${d.id}`)
    .attr("stroke", (d) => linkColor(d.count))
    .attr("stroke-opacity", (d) => linkOpacity(d.count))
    .attr("fill", "none")
    .attr("stroke-width", lineStroke)
    .attr("marker-end", "url(#arrowhead)")
    .style("display", (d) => (d.display === "true" ? "inline" : "none"));

  link
    .append("title")
    .text((d) => `${d.source.id}-${d.target.id} (${d.count})`);

  // 部分瀏覽器 (Firefox), 在 svg 使用 url(##ff00ff-#223344) 連結到 defs 中的 顏色定義會有問題
  // 因此將 hex 中的 # 置換成 _
  const hexToUrlId = (hex) => (hex || "").replace("#", "_");
  // urlId to hex: _ff00ff  => #ff00ff
  const urlIdToHex = (urlId) => (urlId || "").replace("_", "#");

  // get unique group joined colors.
  const nodeGroupColors = [
    ...new Set(
      nodes.map((n) =>
        n.group
          // 將 # 置換成 _, 解決 Firefox 無法讀取 defs 的顏色
          .map((g) => hexToUrlId(groupColor(g)))
          .sort()
          .join("-")
      )
    ),
  ];

  // add node group colors
  const viewGroupColors = view.append("defs");
  nodeGroupColors.forEach((gc) => {
    const linearGradient = viewGroupColors
      .append("linearGradient")
      .attr("id", gc)
      .attr("x1", "0")
      .attr("y1", "0")
      .attr("x2", "0")
      .attr("y2", "1");

    const colors = gc.split("-").map((o) => urlIdToHex(o));
    const numColors = colors.length;
    colors.forEach((c, idx) => {
      linearGradient
        .append("stop")
        .attr("offset", `${(100 * idx) / numColors}%`)
        .attr("stop-opacity", "1")
        .attr("stop-color", c);
      linearGradient
        .append("stop")
        .attr("offset", `${(100 * (idx + 1)) / numColors}%`)
        .attr("stop-opacity", "1")
        .attr("stop-color", c);
    });
  });

  // filter nodes connected to node which is mouse hovered
  const filterNodesConnected = (nodeId, _linksData) => {
    if (_linksData.length === 0) return [];
    const filterNodes = [];
    _linksData.forEach((l) => {
      if (l.source === nodeId) {
        filterNodes.push(l.target);
      }
      if (l.target === nodeId) {
        filterNodes.push(l.source);
      }
    });
    return filterNodes;
  };
  // highlight center node & satellite nodes, dis-highlight unconnected nodes
  const highlightNode = (centerNodeId, nodesConnected, nodeCircleElements) => {
    nodeCircleElements.forEach((item) => {
      const nodeCircleId = item.dataset.id.split("-")[2];
      if (nodeCircleId === centerNodeId) {
        d3.select(item)
          .attr("opacity", nodeCommonStyle.opacityHighlight)
          .attr("data-center-highlight", "true");
      } else if (nodesConnected.includes(nodeCircleId)) {
        d3.select(item)
          .attr("opacity", nodeCommonStyle.opacityHighlight)
          .attr("data-satellite-highlight", "true");
      } else if (!nodesConnected.includes(nodeCircleId)) {
        d3.select(item)
          .attr("fill", nodeCommonStyle.fillColorDisHighlight)
          .attr("data-dis-highlight", "true");
      }
    });
  };
  // set node circle to default
  const setCirclesToDefault = (nodeCircleElements) => {
    nodeCircleElements.forEach((item) => {
      d3.select(item)
        .attr(
          "fill",
          (datum) =>
            `url(#${datum.group
              // 將 # 置換成 _, 解決 Firefox 無法讀取 defs 的顏色
              .map((group) => hexToUrlId(groupColor(group)))
              .sort()
              .join("-")})`
        )
        .attr("opacity", nodeCommonStyle.opacityDefault)
        .attr("data-center-highlight", "false")
        .attr("data-satellite-highlight", "false")
        .attr("data-dis-highlight", "false");
    });
  };
  // handle mouseover circle event
  function handleMouseOverNodeCircle(d, i, g) {
    if (nodeCommonStyle.mouseEvent.mouseover !== "on") return;
    d3.select(this).style("cursor", nodeCommonStyle.nodeCursor.type);
    const nodeId = d3.select(this).attr("data-id").split("-")[2];
    // filter nodes connected to this node
    const nodesConnected = filterNodesConnected(nodeId, data.links);
    // highlight center node & satellite nodes, dis-highlight unconnected nodes
    highlightNode(nodeId, nodesConnected, g);

    if (isFunction(onMouseOverNode)) {
      onMouseOverNode(nodeId);
    }
  }
  // handle mouseout circle event
  function handleMouseOutNodeCircle(d, i, g) {
    if (nodeCommonStyle.mouseEvent.mouseout !== "on") return;
    d3.select(this).style("cursor", "default");
    // set all nodes to default
    setCirclesToDefault(g);
    const nodeId = d3.select(this).attr("data-id").split("-")[2];
    if (isFunction(onMouseOutNode)) {
      onMouseOutNode(nodeId);
    }
  }

  function drawNodeStroke(d, i, g) {
    return !showSubGroup || d.subGroup === undefined
      ? ""
      : subGroupStyle.color[curSubGroups.indexOf(d.subGroup)];
  }

  function drawNodeStrokeDash(d, i, g) {
    return "4 4 12 4";
  }

  const focusNodeCircle = (thisNode) => {
    thisNode
      .classed("focus", true)
      .attr("stroke", "#FF4136")
      .attr("stroke-dasharray", "");
  };

  const unFocusNodeCircle = (thisNode) => {
    thisNode
      .classed("focus", false)
      .attr("stroke", drawNodeStroke)
      .attr("stroke-dasharray", drawNodeStrokeDash);
  };

  // click circle
  function handleMouseClickNodeCircle(d, i, g) {
    if (nodeCommonStyle.mouseEvent.click !== "on") return;
    const selection = d3.select(this);
    const isFocus = selection.classed("focus");
    const nodeId = selection.attr("data-id").split("-")[2];
    if (isFunction(onClickNode)) {
      onClickNode(nodeId, { isFocus: !isFocus, d, selection });
    }
    if (isFocus) {
      unFocusNodeCircle(selection);
    } else {
      focusNodeCircle(selection);
    }
  }

  // click circle label
  function handleMouseClickNodeLabel(d, i, g) {
    if (nodeCommonStyle.mouseEvent.click !== "on") return;
    const selection = d3.select(this);
    const nodeId = selection.attr("data-id").split("-")[2];
    const nodeCircle = d3.select(`[data-id="node-circle-${nodeId}"]`);
    const isNodeCircleFocus = nodeCircle.classed("focus");
    if (isFunction(onClickNode)) {
      onClickNode(nodeId, {
        isFocus: !isNodeCircleFocus,
        d,
        selection,
      });
    }
    if (isNodeCircleFocus) {
      unFocusNodeCircle(nodeCircle);
    } else {
      focusNodeCircle(nodeCircle);
    }
  }

  nodes.sort((a, b) => b.count - a.count);
  // painting nodes
  const node = view
    .append("g")
    .selectAll("circle")
    .data(nodes, (d) => d.id)
    .join("circle")
    .attr("class", "node-circle")
    .attr("data-id", (d) => `node-circle-${d.id}`)
    .attr("data-_id", (d) => `node-circle-${d._id}`)
    .attr("stroke", drawNodeStroke)
    .attr("stroke-width", 5)
    // d.count may equal to 0 when node is 孤立節點 and node count refer to connections with other node
    .attr("r", (d) => nodeSize(d.count) * nodeRadius)
    .attr("stroke-dasharray", drawNodeStrokeDash)
    .attr(
      "fill",
      (d) =>
        `url(#${d.group
          .map((g) => hexToUrlId(groupColor(g)))
          .sort()
          .join("-")})`
    )
    .attr("opacity", nodeCommonStyle.opacityDefault)
    .attr("data-center-highlight", "false")
    .attr("data-satellite-highlight", "false")
    .attr("data-dis-highlight", "false")
    .style("display", (d) => (d.display === "true" ? "inline" : "none"))
    .on("click", handleMouseClickNodeCircle)
    .on("mouseover", handleMouseOverNodeCircle)
    .on("mouseout", handleMouseOutNodeCircle)
    .call(drag(simulation));

  node.append("title").text((d) => `${d.label} (${d.count})`);

  function handleMouseOverNodeLabel() {
    if (nodeCommonStyle.mouseEvent.mouseover !== "on") return;
    d3.select(this).style("cursor", nodeCommonStyle.nodeCursor.type);
    const nodeId = d3.select(this).attr("data-id").split("-")[2];
    // filter nodes connected to this node
    const nodesConnected = filterNodesConnected(nodeId, data.links);
    // highlight center node & satellite nodes, dis-highlight unconnected nodes
    highlightNode(nodeId, nodesConnected, node.nodes());
  }

  function handleMouseOutNodeLabel() {
    if (nodeCommonStyle.mouseEvent.mouseout !== "on") return;
    d3.select(this).style("cursor", "default");
    // set all nodes to default
    setCirclesToDefault(node.nodes());
  }

  // add nodeCount in nodeLabel
  /** don't use arrow function here */
  function insertLinebreaks() {
    const el = d3.select(this);
    el.append("tspan")
      .attr("class", "node-label-node-count")
      // eslint-disable-next-line no-shadow
      .text((d) => `(${d.count})`)
      .style(
        "font-size",
        // eslint-disable-next-line no-shadow
        (d) => `${nodeSize(d.count) * 1.0 + nodeLabelSize}px`
      )
      .style("display", addNodeCount ? "inline" : "none");
    el.append("title").text((d) => `${d.label} (${d.count})`);
  }

  const nodeLabel = view
    .append("g")
    .selectAll("text")
    .data(nodes)
    .join("text")
    .attr("class", "node-label")
    .attr("data-id", (d) => `node-label-${d.id}`)
    .attr("fill", `${nodeStyle.strokeColor}`)
    .attr("text-anchor", "middle")
    .attr("dy", (d) => (nodeSize(d.count) === 1 ? "-0.2em" : "0.5em"))
    .style("font-size", (d) => `${nodeSize(d.count) * 3 + nodeLabelSize}px`)
    .style("text-shadow", `2px 2px 5px ${nodeStyle.shadowColor}`)
    .text((d) => d.label)
    .on("click", handleMouseClickNodeLabel)
    .on("mouseover", handleMouseOverNodeLabel)
    .on("mouseout", handleMouseOutNodeLabel)
    .each(insertLinebreaks)
    .call(drag(simulation));

  // 不管 group 有或沒有成員,都顯示 label
  // legend
  const groupLegend = svg.append("g").attr("class", "group-legend");
  groupLegend
    .selectAll(".legendDots")
    .data(groupsForColorRange) // 使用 groups(包含有或無group成員的資料),才不會影響顏色的排序
    .join("circle")
    .attr("cx", 50)
    // .attr('cx', width - 230) // 放右邊
    .attr("cy", (d, i) => 20 + i * 32)
    .attr("r", 10)
    .style("fill", (d) => groupColor(d));

  groupLegend
    .selectAll(".legendLabel")
    .data(groupsForColorRange) // 使用 groups(包含有或無group成員的資料),才不會影響顏色的排序
    .join("text")
    .attr("font-size", "1.4em")
    .attr("x", 70)
    // .attr('x', width - 210) // 放右邊
    .attr("y", (d, i) => 25 + i * 32)
    .style("fill", (d) => groupColor(d))
    .text((d) => d)
    .attr("text-anchor", "left")
    .style("alignment-baseline", "middle");

  if (datasetTotal > 0) {
    groupLegend
      .append("text")
      .attr("x", 50)
      .attr("y", 20 + groupsForColorRange.length * 32 + 10)
      .attr("font-size", "1.4em")
      .style("fill", "#9280FF")
      .text(`共${datasetTotal}個資料集`)
      .attr("text-anchor", "left")
      .style("alignment-baseline", "middle");
  }

  const legSubGroupPosY = 400;

  const subGroupLegend = svg.append("g").attr("class", "subgroup-legend");
  subGroupLegend
    .selectAll(".legendSubGroup")
    .data(curSubGroups)
    .join("circle")
    .attr("class", "legendSubGroup")
    .attr("cx", 50)
    // .attr('cx', width - 230) // 放右邊
    .attr("cy", (d, i) => legSubGroupPosY + i * 32)
    .attr("r", 10)
    .attr("stroke", (d) => subGroupStyle.color[curSubGroups.indexOf(d)])
    .attr("stroke-width", 3)
    .attr("stroke-dasharray", "4 4 12 4")
    .style("fill", "white")
    .style("display", showSubGroup ? "inline" : "none");

  subGroupLegend
    .selectAll(".legendSubGroupLabel")
    .data(curSubGroups)
    .join("text")
    .attr("class", "legendSubGroupLabel")
    .attr("font-size", "1.5em")
    .attr("x", 70)
    // .attr('x', width - 210) // 放右邊
    .attr("y", (d, i) => legSubGroupPosY + i * 32)
    .style("fill", (d) => subGroupStyle.color[curSubGroups.indexOf(d)])
    .text((d) => d)
    .attr("text-anchor", "left")
    .style("alignment-baseline", "middle")
    .style("display", showSubGroup ? "inline" : "none");

  // events
  simulation.on("tick", () => {
    // 節點位置
    node
      .attr("cx", (d) =>
        Math.max(nodeRadius, Math.min(width - nodeRadius, d.x))
      )
      .attr("cy", (d) =>
        Math.max(nodeRadius, Math.min(height - nodeRadius, d.y))
      );

    // 連接線位置，確保不超過節點邊界並且保持在畫布內
    link.attr("d", (d) => {
      const dx = d.target.x - d.source.x;
      const dy = d.target.y - d.source.y;
      const angle = Math.atan2(dy, dx);
      const sourceRadius = nodeSize(d.source.count) * nodeRadius;
      const targetRadius = nodeSize(d.target.count) * nodeRadius;

      const sourceX = Math.max(
        0,
        Math.min(width, d.source.x + Math.cos(angle) * sourceRadius)
      );
      const sourceY = Math.max(
        0,
        Math.min(height, d.source.y + Math.sin(angle) * sourceRadius)
      );
      const targetX = Math.max(
        0,
        Math.min(width, d.target.x - Math.cos(angle) * targetRadius)
      );
      const targetY = Math.max(
        0,
        Math.min(height, d.target.y - Math.sin(angle) * targetRadius)
      );

      return getLineCurve(
        { x: sourceX, y: sourceY },
        { x: targetX, y: targetY },
        lineRadius
      );
    });

    // 節點標籤位置
    nodeLabel
      .attr("x", (d) =>
        Math.max(nodeRadius * 1.5, Math.min(width - nodeRadius * 1.5, d.x))
      )
      .attr("y", (d) =>
        Math.max(
          nodeRadius * 2,
          Math.min(height - nodeRadius * 0.5, d.y - nodeRadius)
        )
      );
  });

  // eslint-disable-next-line consistent-return
  return {
    svg,
    view,
    link,
    node,
    nodeLabel,
    groupLegend,
    subGroupLegend,
    simulation,
  };
};

export default drawNetwork;
